import { SVGProps } from "react";

const SKIN = "#F2C9A8";
const SKIN_SHADE = "#E5B48F";
const BOOT = "#2E2E3A";
const STEEL = "#2E2E3A";
const HANDLE = "#C98B5B";
const SPARKLE = "M0 -7 L2 -2 L7 0 L2 2 L0 7 L-2 2 L-7 0 L-2 -2 z";

// Gears keep turning slowly: the gear groups are rotated about their own
// origin, so the transform-origin is 0 0 inside each translated group.
const GEAR_CSS = `
@keyframes mw-spin { to { transform: rotate(360deg); } }
@keyframes mw-spin-back { to { transform: rotate(-360deg); } }
.mw-gear-big { animation: mw-spin 28s linear infinite; transform-origin: 0 0; }
.mw-gear-small { animation: mw-spin-back 18s linear infinite; transform-origin: 0 0; }
@media (prefers-reduced-motion: reduce) {
  .mw-gear-big, .mw-gear-small { animation: none; }
}
`;

const Teeth = ({
  count,
  radius,
  width,
  height
}: {
  count: number;
  radius: number;
  width: number;
  height: number;
}) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <rect
        key={i}
        x={-width / 2}
        y={-(radius + height - 6)}
        width={width}
        height={height}
        rx={"3"}
        transform={`rotate(${(i * 360) / count})`}
      />
    ))}
  </>
);

/**
 * Friendly worker in a hard hat and hi-vis vest, toolbox in hand, hammer
 * raised beside a pair of slowly turning gears. Theme colours come from the
 * Chakra colour variables so it reads in both modes; skin, boots and the
 * blush are fixed tones that sit well on either background.
 */
const MaintenanceWorker = (props: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox={"0 0 360 300"}
    width={"360"}
    height={"300"}
    xmlns={"http://www.w3.org/2000/svg"}
    role={"img"}
    aria-label={"A worker in a hard hat raising a hammer beside a large gear"}
    {...props}
  >
    <style>{GEAR_CSS}</style>

    {/* backdrop and ground shadow */}
    <circle
      cx={"190"}
      cy={"150"}
      r={"128"}
      fill={"var(--chakra-colors-grey10)"}
    />
    <ellipse
      cx={"180"}
      cy={"270"}
      rx={"118"}
      ry={"9"}
      fill={"var(--chakra-colors-grey20)"}
      opacity={"0.55"}
    />

    {/* big gear */}
    <g transform={"translate(258 118)"}>
      <g className={"mw-gear-big"}>
        <g fill={"var(--chakra-colors-grey20)"}>
          <Teeth count={10} radius={52} width={18} height={24} />
        </g>
        <circle r={"52"} fill={"var(--chakra-colors-grey20)"} />
        <circle r={"34"} fill={"var(--chakra-colors-grey10)"} />
        <g fill={"var(--chakra-colors-grey20)"}>
          {Array.from({ length: 4 }, (_, i) => (
            <rect
              key={i}
              x={"-5"}
              y={"-34"}
              width={"10"}
              height={"68"}
              rx={"5"}
              transform={`rotate(${i * 45})`}
            />
          ))}
        </g>
        <circle r={"15"} fill={"var(--chakra-colors-grey30)"} />
        <polygon
          points={"0,-8 7,-4 7,4 0,8 -7,4 -7,-4"}
          fill={"var(--chakra-colors-yellow)"}
        />
      </g>
    </g>

    {/* small gear */}
    <g transform={"translate(316 196)"}>
      <g className={"mw-gear-small"}>
        <g fill={"var(--chakra-colors-grey30)"}>
          <Teeth count={8} radius={27} width={12} height={16} />
        </g>
        <circle r={"27"} fill={"var(--chakra-colors-grey30)"} />
        <circle r={"15"} fill={"var(--chakra-colors-grey10)"} />
        <circle r={"6"} fill={"var(--chakra-colors-grey30)"} />
      </g>
    </g>

    {/* sparkles */}
    <g fill={"var(--chakra-colors-yellow)"}>
      <path transform={"translate(196 52)"} d={SPARKLE} />
      <path transform={"translate(318 60) scale(0.7)"} d={SPARKLE} />
      <path transform={"translate(330 140) scale(0.55)"} d={SPARKLE} />
    </g>

    {/* worker */}
    <g transform={"translate(96 62)"}>
      {/* legs and boots */}
      <rect
        x={"18"}
        y={"140"}
        width={"24"}
        height={"58"}
        rx={"9"}
        fill={"var(--chakra-colors-grey30)"}
      />
      <rect
        x={"48"}
        y={"140"}
        width={"24"}
        height={"58"}
        rx={"9"}
        fill={"var(--chakra-colors-grey30)"}
      />
      <path
        d={
          "M10 190 h34 a6 6 0 0 1 6 6 v6 a4 4 0 0 1 -4 4 h-40 a4 4 0 0 1 -4 -4 v-4 a8 8 0 0 1 8 -8 z"
        }
        fill={BOOT}
      />
      <path
        d={
          "M46 190 h34 a6 6 0 0 1 6 6 v6 a4 4 0 0 1 -4 4 h-40 a4 4 0 0 1 -4 -4 v-4 a8 8 0 0 1 8 -8 z"
        }
        fill={BOOT}
      />

      {/* left arm, holding the toolbox */}
      <rect
        x={"-2"}
        y={"78"}
        width={"20"}
        height={"64"}
        rx={"10"}
        fill={"var(--chakra-colors-brand)"}
      />
      <circle cx={"8"} cy={"144"} r={"11"} fill={SKIN} />
      <rect
        x={"-22"}
        y={"156"}
        width={"60"}
        height={"38"}
        rx={"7"}
        fill={"var(--chakra-colors-brand-orange)"}
      />
      <rect
        x={"-22"}
        y={"168"}
        width={"60"}
        height={"4"}
        fill={"#ffffff"}
        opacity={"0.35"}
      />
      <rect
        x={"-4"}
        y={"148"}
        width={"24"}
        height={"10"}
        rx={"5"}
        fill={"var(--chakra-colors-grey30)"}
      />
      <rect
        x={"4"}
        y={"172"}
        width={"8"}
        height={"8"}
        rx={"2"}
        fill={"var(--chakra-colors-grey30)"}
      />

      {/* torso: shirt, hi-vis vest with reflective stripes */}
      <rect
        x={"8"}
        y={"70"}
        width={"74"}
        height={"80"}
        rx={"22"}
        fill={"var(--chakra-colors-brand)"}
      />
      <path
        d={
          "M18 72 h54 a12 12 0 0 1 12 12 v52 a12 12 0 0 1 -12 12 h-54 a12 12 0 0 1 -12 -12 v-52 a12 12 0 0 1 12 -12 z"
        }
        fill={"var(--chakra-colors-yellow)"}
      />
      <path d={"M38 72 h14 v76 h-14 z"} fill={"var(--chakra-colors-brand)"} />
      <rect
        x={"6"}
        y={"104"}
        width={"78"}
        height={"8"}
        fill={"#ffffff"}
        opacity={"0.75"}
      />
      <rect
        x={"6"}
        y={"122"}
        width={"78"}
        height={"8"}
        fill={"#ffffff"}
        opacity={"0.75"}
      />

      {/* right arm, raised with the hammer. Fixed wood and steel tones with
          a light outline on the head: the gear behind is grey in both
          themes, so a grey tool would vanish into it. */}
      <g transform={"translate(78 92) rotate(-62)"}>
        <rect
          x={"0"}
          y={"-10"}
          width={"70"}
          height={"20"}
          rx={"10"}
          fill={"var(--chakra-colors-brand)"}
        />
        <g transform={"translate(66 0)"}>
          <rect
            x={"-14"}
            y={"-6"}
            width={"64"}
            height={"12"}
            rx={"6"}
            fill={HANDLE}
          />
          <rect
            x={"-14"}
            y={"-6"}
            width={"64"}
            height={"4"}
            rx={"2"}
            fill={"#ffffff"}
            opacity={"0.3"}
          />
          <rect
            x={"36"}
            y={"-24"}
            width={"22"}
            height={"48"}
            rx={"6"}
            fill={STEEL}
            stroke={"#ffffff"}
            strokeOpacity={"0.55"}
            strokeWidth={"2"}
          />
          <rect
            x={"52"}
            y={"-20"}
            width={"4"}
            height={"40"}
            rx={"2"}
            fill={"#ffffff"}
            opacity={"0.25"}
          />
        </g>
        <circle cx={"70"} cy={"0"} r={"12"} fill={SKIN} />
      </g>

      {/* neck, head, ears */}
      <rect
        x={"34"}
        y={"56"}
        width={"22"}
        height={"20"}
        rx={"8"}
        fill={SKIN_SHADE}
      />
      <circle cx={"45"} cy={"38"} r={"28"} fill={SKIN} />
      <circle cx={"18"} cy={"42"} r={"5"} fill={SKIN_SHADE} />
      <circle cx={"72"} cy={"42"} r={"5"} fill={SKIN_SHADE} />

      {/* face */}
      <circle
        cx={"34"}
        cy={"40"}
        r={"3.2"}
        fill={"var(--chakra-colors-text)"}
      />
      <circle
        cx={"56"}
        cy={"40"}
        r={"3.2"}
        fill={"var(--chakra-colors-text)"}
      />
      <path
        d={"M33 50 q12 11 24 0"}
        stroke={"var(--chakra-colors-text)"}
        strokeWidth={"2.8"}
        strokeLinecap={"round"}
        fill={"none"}
      />
      <circle cx={"27"} cy={"49"} r={"4"} fill={"#F28B82"} opacity={"0.5"} />
      <circle cx={"63"} cy={"49"} r={"4"} fill={"#F28B82"} opacity={"0.5"} />

      {/* hard hat */}
      <path
        d={"M13 30 a32 32 0 0 1 64 0 z"}
        fill={"var(--chakra-colors-yellow)"}
      />
      <path
        d={"M13 30 a32 32 0 0 1 64 0 h-10 a22 22 0 0 0 -44 0 z"}
        fill={"#E0A800"}
        opacity={"0.5"}
      />
      <rect
        x={"5"}
        y={"27"}
        width={"80"}
        height={"10"}
        rx={"5"}
        fill={"var(--chakra-colors-yellow)"}
      />
      <rect
        x={"40"}
        y={"2"}
        width={"10"}
        height={"26"}
        rx={"5"}
        fill={"#ffffff"}
        opacity={"0.45"}
      />
    </g>
  </svg>
);

export default MaintenanceWorker;
